import { lbsInitConfig, lbsReGeoCodingSearch } from '@hb/map-api';
import { Native } from '@hb/hbridge';
import UBT from 'utils/ubt';
import env from 'utils/env';
import params from '@/utils/params';
import fetch from '../fetch';
import LoadScript from './../../utils/loadScript';
import pkg from '../../../package.json';
import { SilentError } from '../error';
import Bridge from '../nativeProxy/bridge';

lbsInitConfig({
  debug: false, // 默认false。开启之后会有关键信息提示，比如：逆地理缓存命中，小程序会有弹窗提示
  env: env.codeEnv || 'pro',
  contextEnv: 'browser',
  context: window,
  gaode: { key: '11702fd0eda1ab8b2439876a2c20111c' },
  lbs: {
    appKey: 'app_hellobike_oho_internal_page',
    secretKey: 'cdb82506c608536303e0b7ea145ee4cc',
  },
  APM: window.TianQiApm.APM || {},
  UBT: window.TianQiApm.UBT || {},
  // ExtraInfo
  extraInfo: {
    appName: 'AppEvBikeMiniProH5',
    version: pkg.version,
    releaseVersion: pkg.version,
    from: 'h5',
    systemCode: 63,
    __sysTag: '',
  },
});

let _loadPromise;
let _newCityInfoPromise;

function loadMap() {
  if (!_loadPromise) {
    _loadPromise = new Promise((resolve) => {
      window.initAMap = function initAMap() {
        resolve(window.AMap);
      };

      const key = 'cfa73386245e7e570cd4a55915fa144a';
      const mapUrl = `//webapi.amap.com/maps?v=1.4.5&key=${key}&callback=initAMap`;

      const scriptEl = document.createElement('script');
      scriptEl.src = mapUrl;
      document.body.appendChild(scriptEl);
    });
  }
  return _loadPromise;
}

function loadMapUI() {
  return new Promise((resolve) => {
    loadMap().then(() => {
      const mapUIUrl = '//webapi.amap.com/ui/1.0/main.js?callback=initAMapUI';
      LoadScript(mapUIUrl, () => {
        resolve(window.AMapUI);
      });
    });
  });
}

// 初始化native和地图相关的配置
export function initNativeMap() {
  // 如果当前高德Key带有安全密钥，则需要配置以下代码
  window._AMapSecurityConfig = {
    securityJsCode: '6d18761a4ea88e72130130cef0a8f26a',
  };
  // 设置HBridge的自定义高德Key
  window.HBridgeCustomAMapKey = 'd978b462093ab4ea4974bdb05f149c0f';
  // // 设置HBridge的自定义高德Web服务AKey
  window.HBridgeCustomAMapWebKey = '11702fd0eda1ab8b2439876a2c20111c';
  // LBS相关配置
  window.HBridgeCustomLBSConfig = {
    appKey: 'app_hellobike_oho_internal_page',
    secretKey: 'cdb82506c608536303e0b7ea145ee4cc',
  };
  // 优先模式
  window.HBridgeCustomLBSMode = 'HELLO'; // 哈啰LBS优先，高德LBS兜底
  // 或
  // window.HBridgeCustomLBSMode = 'GAODE'; // 高德LBS优先，哈啰LBS兜底
}

const formatRegeocode = (regeocodeItem, { longitude, latitude } = {}) => {
  const {
    formatted_address,
    formattedAddress,
    addressComponent,
  } = regeocodeItem;
  const { adCode, adcode, cityCode, citycode, city } = addressComponent;
  const __adcode = adCode || adcode;
  const __citycode = cityCode || citycode;
  const _adcode = Array.isArray(__adcode) ? '' : __adcode;
  const _citycode = Array.isArray(__citycode) ? '' : __citycode;
  const _city = Array.isArray(city) ? '' : city;
  const formattedRegeocode = {
    ...regeocodeItem,
    formattedAddress: formatted_address || formattedAddress,
    addressComponent: {
      ...addressComponent,
      adcode: _adcode,
      adCode: _adcode,
      citycode: _citycode,
      cityCode: _citycode,
      city: _city,
      accurateCityCode: _citycode,
      accurateAdCode: _adcode,
      formattedAddress: formatted_address || formattedAddress,
      accurateLongitude: longitude,
      accurateLatitude: latitude,
      longitude: addressComponent.longitude || longitude,
      latitude: addressComponent.latitude || latitude,
    },
  };
  return formattedRegeocode;
};

export default class AMapHelper {
  static loadPlugin(map, plugins) {
    return new Promise((resolve) => {
      map.plugin(plugins, resolve.bind(this));
    });
  }

  static createMap(domEl, options) {
    return loadMap().then((AMap) => {
      const map = new AMap.Map(domEl, options);
      return map;
    });
  }

  static destroyMap(map) {
    return loadMap().then((AMap) => {
      map.destroy();
    });
  }

  static addMarker(map, { icon, offset, ...others }) {
    return loadMap().then((AMap) => {
      const { position } = icon;
      if (typeof icon === 'object') {
        const {
          width = 36,
          height = 36,
          x,
          y,
          imgWidth,
          imgHeight,
          image,
        } = icon;
        const opts = {
          size: new AMap.Size(width, height),
        };
        if (image) opts.image = image;

        if (x !== undefined && y !== undefined)
          opts.imageOffset = new AMap.Pixel(x, y);

        if (imgWidth !== undefined && imgHeight !== undefined) {
          opts.imageSize = new AMap.Size(imgWidth, imgHeight);
          opts.size = new AMap.Size(imgWidth, imgHeight);
        }

        icon = new AMap.Icon(opts);
      }

      if (offset && offset[0] !== undefined && offset[1] !== undefined) {
        offset = new AMap.Pixel(offset[0], offset[1]);
      }

      const marker = new AMap.Marker({
        icon,
        offset,
        position: new AMap.LngLat(position[0], position[1]),
        ...others,
      });

      map.add(marker);

      return marker;
    });
  }

  static addMarkers(map, markers) {
    return loadMap().then((AMap) => {
      const markerArr = [];
      markers.forEach(({ icon, offset, ...others }) => {
        let { position } = icon;
        if (typeof icon === 'object') {
          const {
            width = 36,
            height = 36,
            x,
            y,
            imgWidth,
            imgHeight,
            image,
          } = icon;
          const opts = {
            size: new AMap.Size(width, height),
          };
          if (image) opts.image = image;

          if (x !== undefined && y !== undefined)
            opts.imageOffset = new AMap.Pixel(x, y);

          if (imgWidth !== undefined && imgHeight !== undefined) {
            opts.imageSize = new AMap.Size(imgWidth, imgHeight);
            opts.size = new AMap.Size(imgWidth, imgHeight);
          }

          icon = new AMap.Icon(opts);
        }

        if (offset && offset[0] !== undefined && offset[1] !== undefined) {
          offset = new AMap.Pixel(offset[0], offset[1]);
        }

        markerArr.push(
          new AMap.Marker({
            icon,
            offset,
            position: new AMap.LngLat(position[0], position[1]),
            ...others,
          }),
        );
      });

      map.add(markerArr);

      return markerArr;
    });
  }

  static async geoDecode(longitude, latitude) {
    try {
      const res = await lbsReGeoCodingSearch(
        {
          locations: [{ lng: longitude, lat: latitude }],
        },
        {
          // !!!逆地理编码，需要设置该选项。哈罗LBS服务 -> 高德服务兜底
          serveSort: ['HELLO', 'GAODE'],
        },
      );
      console.log('geoDecode-result==========================', res);
      if (res && res.data) {
        const { status, data } = res.data;
        if (Number(status) !== 1) {
          throw new SilentError('LBS逆地理编码状态错误');
        }
        let result = {};
        if (data && data.reGeoCodesList && data.reGeoCodesList.length > 0) {
          result = formatRegeocode(data.reGeoCodesList[0], {
            longitude,
            latitude,
          });
        } else {
          throw new SilentError('LBS逆地理数据不完整');
        }
        return result;
      } else {
        throw new SilentError('LBS逆地理数据不完整');
      }
    } catch (e) {
      throw new SilentError('LBS逆地理数据不完整');
    }
  }

  static async getCurrentLocation(options) {
    const { needDefaultCity = true } = options || {};
    let data = {};
    try {
      console.info(
        'env-getCurrentLocation========================',
        env.isWeixin,
        env.isWxMP,
      );
      if (env.isWeixin || env.isWxMP) {
        console.info('env-wechat-location-start=======================');
        const { longitude, latitude } = await Bridge.getLocation();
        console.info(
          'env-wechat-location-result=======================',
          longitude,
          latitude,
        );
        const result = await this.geoDecode(longitude, latitude);
        data = (result || {}).addressComponent;
        console.info(
          'env-wechat-location-geoDecode=======================',
          data,
        );
      } else {
        console.info('env-browser-getCityInfo-start=======================');
        data = (await Native.getCityInfo()) || {};
        console.info(
          'env-browser-getCityInfo-result=======================',
          data,
        );
      }

      console.log('getCurrentLocation-result==========================', data);
      const { longitude, latitude, adcode, adCode, citycode, cityCode } =
        data || {};
      // @ts-ignore
      const getAdcode = adcode || adCode;
      // @ts-ignore
      const getCityCode = citycode || cityCode;
      if (!getAdcode || !getCityCode) {
        const adcode = params.get('adCode', window.location.href);
        const citycode = params.get('cityCode', window.location.href);
        // 当定位失败的时候，cityCode尝试从路由中取
        data = Object.assign({}, data, {
          adcode,
          citycode,
          adCode: getAdcode || adcode,
          cityCode: getCityCode || citycode,
          longitude,
          latitude,
        });
        if (needDefaultCity) {
          // 需要兜底的情况 购卡页不需要没有定位信息的情况走到默认兜底城市
          if (!adcode || !citycode) {
            // 最终兜底萨嘎
            data = {
              province: '萨嘎',
              cityCode: '0892',
              adCode: '540236',
              longitude: 85.23325,
              latitude: 29.32848,
            };
          }
        }
      }
    } catch (e) {
      data = {
        province: '萨嘎',
        cityCode: '0892',
        adCode: '540236',
        longitude: 85.23325,
        latitude: 29.32848,
      };
    }
    return data;
  }

  static getCityInfo() {
    if (_newCityInfoPromise) {
      return _newCityInfoPromise;
    }
    _newCityInfoPromise = new Promise((resolve, reject) => {
      const key = 'cfa73386245e7e570cd4a55915fa144a';
      const mapUrl = `https://webapi.amap.com/maps/ipCity?key=${key}`;
      try {
        fetch(mapUrl).then((res) => {
          try {
            const body = res.response.match(/\(([^)]*)\)/);
            const result = JSON.parse(body[1]);
            resolve(result);
          } catch (e) {
            reject(new Error('定位失败'));
          }
        });
      } catch (e) {
        reject(new Error('定位失败'));
      }
    });
    return _newCityInfoPromise;
  }

  static drivingNavigation({ origin, destination }) {
    return new Promise((resolve, reject) => {
      loadMap().then((AMap) => {
        AMap.service('AMap.Driving', () => {
          const driving = new AMap.Driving({
            policy: AMap.DrivingPolicy.LEAST_TIME,
          });
          driving.searchOnAMAP({
            origin: new AMap.LngLat(origin.lng, origin.lat),
            destination: new AMap.LngLat(destination.lng, destination.lat),
          });
          resolve();
        });
      }, reject);
    });
  }

  static searchPlaces(keyword) {
    return new Promise((resolve, reject) => {
      loadMap().then((AMap) => {
        AMap.service('AMap.PlaceSearch', () => {
          const placeSearch = new AMap.PlaceSearch({
            city: '上海',
          });
          placeSearch.search(keyword, (status, result) => {
            if (status === 'complete') {
              resolve(result);
            } else if (status === 'error') {
              reject(result.info);
            } else if (status === 'no_data') {
              // eslint-disable-next-line prefer-promise-reject-errors
              reject('no data');
            }
          });
        });
      });
    });
  }

  static zoomFit({ map, center, visibleLocation, offset = { x: 0, y: 0 } }) {
    map.setCenter(center);
    const zoom = map.getZoom();
    const { lng, lat } = visibleLocation;
    const mapSize = map.getSize();
    const mapWidth = mapSize.getWidth();
    const mapHeight = mapSize.getHeight();
    for (let i = zoom; i > 3; i--) {
      const result = map.lngLatToContainer([lng, lat], i);
      if (
        result.x > offset.x &&
        result.y > offset.y &&
        result.x < mapWidth - offset.x &&
        result.y < mapHeight - offset.y
      ) {
        if (i !== zoom) {
          map.setZoom(i);
        }
        break;
      }
    }
  }

  static setCenter(map, centerPoint) {
    map.setCenter(centerPoint);
  }

  static getCity(map) {
    return new Promise((resolve, reject) => {
      map.getCity((data) => {
        if (data) {
          resolve(data);
        } else {
          reject();
        }
      });
    });
  }

  static caculateDistance(pos1, pos2) {
    return loadMap().then((AMap) =>
      new AMap.LngLat(pos1.lng, pos1.lat).distance([pos2.lng, pos2.lat]),
    );
  }
  static AMapUI() {
    return loadMapUI()
      .then((AMapUI) => {
        return AMapUI;
      })
      .catch((e) => {
        // eslint-disable-next-line
        console.log(e);
      });
  }

  static drawPolygon(map, options) {
    return loadMap().then((AMap) => {
      // 多边形轮廓线的节点坐标数组;
      const { originPath = '', ...others } = options || {};
      const path = [];
      let originArr = originPath;

      if (typeof originPath === 'string') {
        originArr = originPath.split(',');
      }

      originArr.forEach((item) => {
        const itemArr = item.split(' ') || [];
        if (itemArr[0] !== undefined && itemArr[1] !== undefined) {
          path.push([itemArr[0], itemArr[1]]);
        }
      });

      const polygon = new AMap.Polygon({
        path,
        strokeColor: '#0B82F1',
        strokeOpacity: 0.46,
        fillColor: '#0B82F1',
        fillOpacity: 0.1,
        strokeWeight: 2,
        ...others,
      });

      map.add(polygon);

      return polygon;
    });
  }

  static drawPolyline(map, options) {
    return loadMap().then((AMap) => {
      const polygon = new AMap.Polyline({
        strokeWeight: 2,
        strokeColor: '#666666',
        strokeStyle: 'solid',
        ...options,
      });

      map.add(polygon);

      return polygon;
    });
  }
}
